import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SUPPORTED_LANGS } from "@/i18n";
import { stripLangFromPath } from "@/components/LangSync";

export default function LanguageSwitcher({ variant = "navbar" }) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current =
    SUPPORTED_LANGS.find((l) => l.code === i18n.resolvedLanguage) ||
    SUPPORTED_LANGS[0];

  const handleChange = (code) => {
    i18n.changeLanguage(code);
    // Navigate to the localised URL so Google (and the user) can deep-link
    // straight into the translated tree. English stays at the root; French &
    // Portuguese get /fr and /pt prefixes.
    const basePath = stripLangFromPath(location.pathname);
    const target =
      code === "en"
        ? basePath || "/"
        : basePath === "/"
          ? `/${code}`
          : `/${code}${basePath}`;
    navigate(target + location.search + location.hash);
    setOpen(false);
  };

  const isDark = variant === "dark";

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-switcher-trigger"
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-bold transition-colors ${
          isDark
            ? "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-brand hover:text-white"
            : "border-outline-variant bg-white/70 backdrop-blur text-on-surface hover:border-brand hover:text-brand"
        }`}
      >
        <span aria-hidden="true" className="text-sm leading-none">
          {current.flag}
        </span>
        <span className="font-mono tracking-widest">{current.short}</span>
        <span
          className={`material-symbols-outlined text-[14px] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        >
          expand_more
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            data-testid="language-switcher-menu"
            className="absolute right-0 top-full mt-2 min-w-[160px] rounded-xl bg-white dark:bg-zinc-950 border border-outline-variant shadow-[0_15px_40px_-12px_rgba(0,0,0,0.18)] overflow-hidden z-50"
          >
            {SUPPORTED_LANGS.map((lang) => {
              const active = lang.code === current.code;
              return (
                <li key={lang.code}>
                  <button
                    type="button"
                    onClick={() => handleChange(lang.code)}
                    role="option"
                    aria-selected={active}
                    data-testid={`lang-option-${lang.code}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors ${
                      active
                        ? "bg-tertiary-fixed text-brand font-bold"
                        : "text-on-surface hover:bg-surface-container-low"
                    }`}
                  >
                    <span className="text-base leading-none" aria-hidden="true">
                      {lang.flag}
                    </span>
                    <span className="flex-1">{lang.label}</span>
                    {active && (
                      <span className="material-symbols-outlined text-brand text-[16px]">
                        check
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
